import { Heading, PrimaryButton, SecondaryButton, Tooltip } from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useParams } from 'react-router';

import { AiSubtitlesInfo } from './ai-subtitles-info';
import { BasicEventInfo } from './basic-event-info';
import { EncoderInfo } from './encoder-info';
import { RecordingInfo } from './recording-info';
import { SharingInfo } from './sharing-info';
import {
  GetWebcastSetupSummaryDocument as GET_WEBCAST_SETUP_SUMMARY,
  State,
  useGetWebcastSetupSummaryQuery,
  useInitializeEventMutation,
} from '../../../../generated/graphql-manager';
import { useAbsoluteRoutes } from '../../../../routes';
import { addParamToPath, devLog, useViewerLink } from '../../../../utils';
import { useUserPermissions } from '../../../hooks/user-permissions';

import styles from './setup-summary.module.css';
import managerStyles from '../../../manager.module.css';

export function SetupSummaryPage() {
  const { webcastId = '' } = useParams();
  const { t } = useTranslation();
  const routes = useAbsoluteRoutes();
  const {
    isAccessToOperatorAllowed,
    isAccessToOperatorMessagingAllowed,
    isFullAccessToOperatorAllowed,
    isEditorAndModerator,
  } = useUserPermissions();

  const { data } = useGetWebcastSetupSummaryQuery({ variables: { webcastId }, skip: !webcastId });
  const { state, sharingLink } = data?.webcast || {};

  const [isRetrying, setIsRetrying] = useState(false);
  const retryIntervalRef = useRef<number | null>(null);

  const [initializeEventMutation, { loading, called }] = useInitializeEventMutation({
    refetchQueries: [GET_WEBCAST_SETUP_SUMMARY],
    onCompleted: (data) => {
      if (data.initializeEvent.__typename === 'InitializeEventSuccess') {
        setIsRetrying(false);
        if (retryIntervalRef.current) {
          window.clearInterval(retryIntervalRef.current);
          retryIntervalRef.current = null;
        }
      } else if (data.initializeEvent.__typename === 'InitializeEventProblem') {
        if (data.initializeEvent.code === 'OPERATION_ALREADY_IN_PROGRESS') {
          devLog('Initialization already in progress, will retry...');
          setIsRetrying(true);
        } else {
          setIsRetrying(false);
          if (retryIntervalRef.current) {
            window.clearInterval(retryIntervalRef.current);
            retryIntervalRef.current = null;
          }
        }
      }
    },
    onError: () => {
      setIsRetrying(false);
      if (retryIntervalRef.current) {
        window.clearInterval(retryIntervalRef.current);
        retryIntervalRef.current = null;
      }
    },
  });

  useEffect(() => {
    if (state === State.PRELIVE && isFullAccessToOperatorAllowed && webcastId && !loading && !called && !isRetrying) {
      initializeEventMutation({ variables: { webcastId } });
    }
  }, [webcastId, state, isFullAccessToOperatorAllowed, initializeEventMutation, loading, called, isRetrying]);

  useEffect(() => {
    if (isRetrying && !retryIntervalRef.current) {
      retryIntervalRef.current = window.setInterval(() => {
        initializeEventMutation({ variables: { webcastId } });
      }, 2000);
    }

    return () => {
      if (retryIntervalRef.current) {
        window.clearInterval(retryIntervalRef.current);
        retryIntervalRef.current = null;
      }
    };
  }, [isRetrying, webcastId, initializeEventMutation]);

  const operatorLinkDisabled =
    loading || isRetrying || (state === State.PRELIVE && !called) || !isAccessToOperatorAllowed;
  const viewerLink = useViewerLink(sharingLink);

  return (
    <main className={managerStyles.main} data-testid="setup-summary-page">
      <Heading className={managerStyles.grayText}>{t('manager.webcastSetup.summary.title')}</Heading>

      <div className={styles.wrapper}>
        <BasicEventInfo />
        <SharingInfo />
        <RecordingInfo />
        <EncoderInfo />
        <AiSubtitlesInfo />
      </div>

      <div className={managerStyles.buttons}>
        <Tooltip
          data-testid="open-operator-tooltip"
          label={t('manager.webcastSetup.notEditableMessage.role')}
          hidden={(isAccessToOperatorAllowed && !isEditorAndModerator) || isFullAccessToOperatorAllowed}
        >
          <PrimaryButton
            data-testid="open-operator"
            component={NavLink}
            to={addParamToPath(routes.operator, webcastId)}
            disabled={operatorLinkDisabled || (isEditorAndModerator && !isFullAccessToOperatorAllowed)}
            rel="noreferrer"
            target="_blank"
          >
            {t('manager.webcastSetup.summary.buttons.openOperator')}
          </PrimaryButton>
        </Tooltip>

        <SecondaryButton
          data-testid="open-viewer"
          component={NavLink}
          to={viewerLink}
          disabled={!viewerLink}
          rel="noreferrer"
          target="_blank"
        >
          {t('manager.webcastSetup.summary.buttons.openViewer')}
        </SecondaryButton>

        {isEditorAndModerator && (
          <Tooltip
            data-testid="open-operator-messaging-tooltip"
            label={t('manager.webcastSetup.notEditableMessage.role')}
            hidden={isAccessToOperatorMessagingAllowed}
          >
            <SecondaryButton
              data-testid="open-operator-messaging"
              component={NavLink}
              to={addParamToPath(routes.operator_messaging, webcastId)}
              disabled={!isAccessToOperatorMessagingAllowed}
            >
              {t('manager.webcastSetup.summary.buttons.openOperatorMessaging')}
            </SecondaryButton>
          </Tooltip>
        )}
      </div>
    </main>
  );
}
